import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx } from "./Primitives";
import type { Deal, Market, Org, Partner, Suggestion } from "../types";

export interface IntelNote {
  id: string; title: string; summary: string | null; story: string | null; relevance: string | null; url: string | null;
  source: string | null; country: string | null; market: string | null; deal_id: string | null; org_id: string | null;
  note: string | null; deal_name: string | null; org_name: string | null; saved_by_name: string | null; created_at: string;
}

const day = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** "Save to CRM": the user decides where an accepted Radar story goes. */
export function AcceptDialog({ s, partners, me, onClose, onSaved }: {
  s: Suggestion; partners: Record<string, Partner>; me: string; onClose: () => void; onSaved: (msg: string) => void;
}) {
  const { data: deals } = useData<Deal[]>("/api/deals");
  const { data: orgs } = useData<Org[]>("/api/orgs");
  const { data: markets } = useData<Market[]>("/api/markets");
  const matchMarket = (markets || []).find(m => m.name.toLowerCase() === (s.country || s.deal_market || "").toLowerCase())?.name;
  const [dealId, setDealId] = useState(s.deal_id || "");
  const [orgId, setOrgId] = useState("");
  const [market, setMarket] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [withTask, setWithTask] = useState(s.kind === "task" && !!s.task);
  const [task, setTask] = useState(s.task || "");
  const [owner, setOwner] = useState(s.owner || me);
  const [due, setDue] = useState(s.due ? String(s.due).slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newOrg, setNewOrg] = useState({ name: "", type: "Operator" });
  const [asLead, setAsLead] = useState(false);
  const [lead, setLead] = useState({ name: "", next_step: "" });
  const [filled, setFilled] = useState<"loading" | "ai" | "text" | null>("loading");
  const mk = market ?? matchMarket ?? "";
  const isIntel = s.source_kind === "intel" || s.source_kind === "signal";

  // Pre-fill as much as possible from the story (AI when configured).
  useEffect(() => {
    let live = true;
    api.get<{ deal_id: string | null; org_id: string | null; market: string | null; new_company: { name: string; type: string } | null;
      task: string; owner: string | null; due: string | null; note: string; ai: boolean }>(`/api/suggestions/${s.id}/prefill`)
      .then(p => {
        if (!live) return;
        if (p.deal_id) setDealId(d => d || p.deal_id!);
        if (p.org_id) setOrgId(o => o || p.org_id!);
        else if (p.new_company) { setOrgId(o => o || "__new"); setNewOrg(p.new_company); }
        if (p.market) setMarket(m => m ?? p.market);
        if (p.note) setNote(n => n || p.note);
        if (p.task) { setTask(t => t || p.task); setWithTask(true); }
        if (p.owner) setOwner(p.owner);
        if (p.due) setDue(d => d || String(p.due).slice(0, 10));
        const company = p.new_company?.name || "";
        setLead(l => ({ name: l.name || company, next_step: l.next_step || p.task || "" }));
        setFilled(p.ai ? "ai" : "text");
      })
      .catch(() => live && setFilled(null));
    return () => { live = false; };
  }, [s.id]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const accountName = orgId === "__new" ? newOrg.name.trim() : (orgs || []).find(o => o.id === orgId)?.name || "";
      await api.put(`/api/suggestions/${s.id}/accept`, {
        deal_id: dealId || null, org_id: orgId && orgId !== "__new" ? orgId : null, market: mk || null, note: note || null,
        new_org: orgId === "__new" && newOrg.name.trim() ? newOrg : null,
        target: asLead && (lead.name.trim() || accountName) ? {
          name: lead.name.trim() || accountName, kind: orgId === "__new" ? newOrg.type : "Operator", market: mk || null,
          angle: s.title, next_step: lead.next_step || null,
        } : null,
        task: withTask && task.trim() ? { task, owner, due: due || null } : null,
      });
      const where = [
        dealId && `deal “${(deals || []).find(d => d.id === dealId)?.name || ""}”`,
        orgId && `account ${orgId === "__new" ? newOrg.name + " (new)" : (orgs || []).find(o => o.id === orgId)?.name || ""}`,
        mk && `market ${mk}`,
      ].filter(Boolean).join(", ");
      onSaved(`Saved${isIntel ? " to Saved intel" : ""}${where ? ` — filed under ${where}` : ""}${withTask && task.trim() ? ", a task was created" : ""}${asLead ? ", and added as a lead (relationship target)" : ""}.`);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };

  return (
    <div className="fd-scrim" onMouseDown={onClose}>
      <div className="fd-panel" role="dialog" aria-label="Save to CRM" onMouseDown={e => e.stopPropagation()}>
        <div className="fd-head">
          <div>
            <div className="fd-title">Save to CRM</div>
            <div className="fd-sub">{s.title}</div>
          </div>
          <button className="kbd-chip fd-esc" onClick={onClose}>esc</button>
        </div>
        <div className="fd-body">
          {isIntel && (
            <p className="rs-explain">
              Saga's write-up{s.url ? <> (<a href={s.url} target="_blank" rel="noreferrer">read it</a>)</> : null} is saved in the CRM under <strong>Saved intel</strong>.
              Choose where it belongs — it will also show on that deal's timeline and in that account's communications.
            </p>
          )}
          {filled === "loading" && <div className="rs-filling">{I.ai({ size: 13 })} Reading the story to fill this in…</div>}
          {filled === "ai" && <div className="rs-filling done">{I.ai({ size: 13 })} Filled in by Saga AI from the story — check before saving.</div>}
          {filled === "text" && <div className="rs-filling done">Filled in from names found in the story — check before saving.</div>}
          <div className="set-grid">
            <label className="set-field">
              <span className="set-label">Deal</span>
              <select value={dealId} onChange={e => setDealId(e.target.value)}>
                <option value="">— not linked to a deal —</option>
                {(deals || []).map(d => <option key={d.id} value={d.id}>{d.name}{d.market ? ` · ${d.market}` : ""}</option>)}
              </select>
            </label>
            <label className="set-field">
              <span className="set-label">Account</span>
              <select value={orgId} onChange={e => setOrgId(e.target.value)}>
                <option value="">— none —</option>
                {(orgs || []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                <option value="__new">+ New account…</option>
              </select>
            </label>
            {orgId === "__new" && (
              <>
                <label className="set-field">
                  <span className="set-label">New account name</span>
                  <input value={newOrg.name} onChange={e => setNewOrg({ ...newOrg, name: e.target.value })} placeholder="Company name" />
                </label>
                <label className="set-field">
                  <span className="set-label">Type</span>
                  <select value={newOrg.type} onChange={e => setNewOrg({ ...newOrg, type: e.target.value })}>
                    {["Operator", "NOC", "Government", "Regulator", "Contractor", "Investor", "Other"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </>
            )}
            <label className="set-field">
              <span className="set-label">Market</span>
              <select value={mk} onChange={e => setMarket(e.target.value)}>
                <option value="">— none —</option>
                {(markets || []).map(m => <option key={m.slug} value={m.name}>{m.name}</option>)}
              </select>
            </label>
            <label className="set-field wide">
              <span className="set-label">Your note (optional)</span>
              <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Why it matters to us, who to talk to…" />
            </label>
            <label className="set-check wide-check">
              <input type="checkbox" checked={asLead} onChange={e => setAsLead(e.target.checked)} />
              Also add as a lead — a relationship target to approach (Tasks → Relationship targets)
            </label>
            {asLead && (
              <>
                <label className="set-field">
                  <span className="set-label">Lead (company)</span>
                  <input value={lead.name} onChange={e => setLead({ ...lead, name: e.target.value })}
                    placeholder={orgId === "__new" ? newOrg.name : (orgs || []).find(o => o.id === orgId)?.name || "Company to approach"} />
                </label>
                <label className="set-field">
                  <span className="set-label">First step</span>
                  <input value={lead.next_step} onChange={e => setLead({ ...lead, next_step: e.target.value })} placeholder="e.g. Ask Trond for an intro" />
                </label>
              </>
            )}
            <label className="set-check wide-check">
              <input type="checkbox" checked={withTask} onChange={e => setWithTask(e.target.checked)} /> Also create a task
            </label>
            {withTask && (
              <>
                <label className="set-field wide">
                  <span className="set-label">Task</span>
                  <input value={task} onChange={e => setTask(e.target.value)} placeholder="e.g. Call Eni's Mozambique project team about the November wells" />
                </label>
                <label className="set-field">
                  <span className="set-label">Owner</span>
                  <select value={owner} onChange={e => setOwner(e.target.value)}>
                    {Object.values(partners).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                <label className="set-field">
                  <span className="set-label">Due</span>
                  <input type="date" value={due} onChange={e => setDue(e.target.value)} />
                </label>
              </>
            )}
          </div>
          {err && <div className="set-notice err fd-err">{err}</div>}
        </div>
        <div className="fd-foot">
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" disabled={busy || (withTask && !task.trim()) || (orgId === "__new" && !newOrg.name.trim())} onClick={save}>
            {I.check({ size: 13, w: 2.2 })}{busy ? "Saving…" : "Save to CRM"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The CRM's own intelligence library: everything accepted from the Radar. */
export function SavedIntel({ go }: { go: (screen: string, id?: string) => void }) {
  const [market, setMarket] = useState("");
  const [q, setQ] = useState("");
  const params = new URLSearchParams();
  if (market) params.set("market", market);
  if (q.trim()) params.set("q", q.trim());
  const { data: items, refetch } = useData<IntelNote[]>(`/api/intel${params.toString() ? "?" + params : ""}`);
  const { data: markets } = useData<Market[]>("/api/markets");
  const [refile, setRefile] = useState<IntelNote | null>(null);

  return (
    <div className="rs-saved">
      <div className="ld-filters">
        <select className="ld-select" value={market} onChange={e => setMarket(e.target.value)} aria-label="Market">
          <option value="">All markets</option>
          {(markets || []).map(m => <option key={m.slug} value={m.name}>{m.name}</option>)}
        </select>
        <input className="set-search ld-search" placeholder="Search saved intelligence…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      {items && items.length === 0 && (
        <div className="card ld-empty"><p>Nothing saved yet. Accept a story on the Radar to keep Saga's write-up here, filed under a deal, account or market.</p></div>
      )}
      <div className="rs-list">
        {(items || []).map(i => (
          <div key={i.id} className="card rs-item">
            <div className="rs-top">
              {i.market && <span className="rs-mkt">{i.market}</span>}
              <span className="ld-when">{day(i.created_at)}{i.saved_by_name ? ` · ${i.saved_by_name}` : ""}</span>
            </div>
            <div className="rs-title">{i.title}</div>
            {i.summary && <div className="rs-sum">{i.summary}</div>}
            {i.relevance && <div className="rs-rel"><strong>Saga relevance:</strong> {i.relevance}</div>}
            {i.note && <div className="rs-note">“{i.note}”</div>}
            <FullStory kind="intel" id={i.id} url={i.url} />
            <div className="rs-foot">
              {i.deal_id && <button className="rep-chip" onClick={() => go("project", i.deal_id!)}>{I.pipeline({ size: 12 })}{i.deal_name}</button>}
              {i.org_id && <button className="rep-chip op" onClick={() => go("clients", i.org_id!)}>{i.org_name}</button>}
              <span style={{ flex: 1 }} />
              <button className="btn ghost sm" onClick={() => setRefile(i)}>Re-file</button>
              <button className="icon-btn nl-del" title="Delete" onClick={async () => {
                if (!confirm(`Delete “${i.title}” from Saved intel?`)) return;
                await api.delete(`/api/intel/${i.id}`); refetch();
              }}>{I.trash({ size: 14 })}</button>
            </div>
          </div>
        ))}
      </div>
      {refile && <RefileDialog note={refile} onClose={() => setRefile(null)} onSaved={() => { setRefile(null); refetch(); }} />}
    </div>
  );
}

function RefileDialog({ note, onClose, onSaved }: { note: IntelNote; onClose: () => void; onSaved: () => void }) {
  const { data: deals } = useData<Deal[]>("/api/deals");
  const { data: orgs } = useData<Org[]>("/api/orgs");
  const { data: markets } = useData<Market[]>("/api/markets");
  const [f, setF] = useState({ deal_id: note.deal_id || "", org_id: note.org_id || "", market: note.market || "", note: note.note || "" });
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="fd-scrim" onMouseDown={onClose}>
      <div className="fd-panel" onMouseDown={e => e.stopPropagation()}>
        <div className="fd-head"><div><div className="fd-title">Re-file</div><div className="fd-sub">{note.title}</div></div></div>
        <div className="fd-body">
          <div className="set-grid">
            <label className="set-field"><span className="set-label">Deal</span>
              <select value={f.deal_id} onChange={e => setF({ ...f, deal_id: e.target.value })}>
                <option value="">— none —</option>{(deals || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select></label>
            <label className="set-field"><span className="set-label">Account</span>
              <select value={f.org_id} onChange={e => setF({ ...f, org_id: e.target.value })}>
                <option value="">— none —</option>{(orgs || []).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></label>
            <label className="set-field"><span className="set-label">Market</span>
              <select value={f.market} onChange={e => setF({ ...f, market: e.target.value })}>
                <option value="">— none —</option>{(markets || []).map(m => <option key={m.slug} value={m.name}>{m.name}</option>)}
              </select></label>
            <label className="set-field wide"><span className="set-label">Note</span>
              <textarea rows={2} value={f.note} onChange={e => setF({ ...f, note: e.target.value })} /></label>
          </div>
          {err && <div className="set-notice err fd-err">{err}</div>}
        </div>
        <div className="fd-foot"><span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" onClick={async () => {
            try { await api.put(`/api/intel/${note.id}`, f); onSaved(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
          }}>Save</button>
        </div>
      </div>
    </div>
  );
}

/** "Read full story": Saga's whole write-up, fetched from info.saga-advisory.com if not stored yet. */
export function FullStory({ kind, id, url }: { kind: "suggestions" | "intel"; id: string; url?: string | null }) {
  const [open, setOpen] = useState(false);
  const [st, setSt] = useState<{ story: string; relevance: string | null; url: string | null } | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "missing">("idle");
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (st || state === "loading") return;
    setState("loading");
    try { setSt(await api.get(`/api/${kind}/${id}/story`)); setState("idle"); } catch { setState("missing"); }
  };
  const link = st?.url || url;
  return (
    <div className="fs-wrap" onClick={e => e.stopPropagation()}>
      <div className="fs-links">
        <button className="ld-link" style={{ marginLeft: 0 }} onClick={toggle}>{open ? "Hide full story" : "Read full story"}</button>
        {link && <a className="ld-link" href={link} target="_blank" rel="noreferrer">Open on info.saga-advisory.com ↗</a>}
      </div>
      {open && (
        <div className="rs-story">
          {state === "loading" && <p className="set-hint">Fetching Saga's story…</p>}
          {state === "missing" && <p className="set-hint">Saga hasn't written this one up yet{link ? " — try the link above" : ""}.</p>}
          {st && st.story.split(/\n{2,}/).map((p, k) => <p key={k}>{p}</p>)}
        </div>
      )}
    </div>
  );
}

export const radarTabClass = (active: boolean) => clsx("seg-btn", active && "active");
